/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ContactDisplay, ContactRoleType, EmployeePayroll } from '@gh/builder-shared-data';
import { compact } from 'lodash';
import { nullate } from '@gh/core-util';
import { ContactDetailsCommon } from './resources/model/contact-details-common.model';
import { ContactDetailsEmployeeSalary } from './resources/model/contact-details-employee.model';
import { CountryCode, parsePhoneNumberWithError } from 'libphonenumber-js';
import { ContactDetails } from './resources/model/contact-details.model';

export interface CountryCodeVal {
  id: string;
  code: string;
  name: string;
}

export const COUNTRY_CODES = new Map<string, CountryCodeVal>([
  ['AU', { id: 'AU', code: '+61', name: 'Australia' }],
  ['US', { id: 'US', code: '+1', name: 'United States/Canada' }],
  ['NZ', { id: 'NZ', code: '+64', name: 'New Zealand' }],
  ['IN', { id: 'IN', code: '+91', name: 'India' }],
  ['GB', { id: 'GB', code: '+44', name: 'United Kingdom' }],
  ['DE', { id: 'DE', code: '+49', name: 'Germany' }],
  ['FR', { id: 'FR', code: '+33', name: 'France' }],
  ['IT', { id: 'IT', code: '+39', name: 'Italy' }],
  ['ES', { id: 'ES', code: '+34', name: 'Spain' }],
  ['BR', { id: 'BR', code: '+55', name: 'Brazil' }],
  ['MX', { id: 'MX', code: '+52', name: 'Mexico' }],
  ['JP', { id: 'JP', code: '+81', name: 'Japan' }],
  ['CN', { id: 'CN', code: '+86', name: 'China' }],
  ['RU', { id: 'RU', code: '+7', name: 'Russia' }],
  ['ZA', { id: 'ZA', code: '+27', name: 'South Africa' }],
  ['SG', { id: 'SG', code: '+65', name: 'Singapore' }],
  ['AE', { id: 'AE', code: '+971', name: 'United Arab Emirates' }],
  ['NL', { id: 'NL', code: '+31', name: 'Netherlands' }],
  ['SE', { id: 'SE', code: '+46', name: 'Sweden' }],
  ['NO', { id: 'NO', code: '+47', name: 'Norway' }],
  ['DK', { id: 'DK', code: '+45', name: 'Denmark' }],
  ['FI', { id: 'FI', code: '+358', name: 'Finland' }],
  ['IE', { id: 'IE', code: '+353', name: 'Ireland' }],
  ['PH', { id: 'PH', code: '+63', name: 'Philippines' }],
]);

export function getCountriesList(): CountryCodeVal[] {
  return Array.from(COUNTRY_CODES.values());
}

export function getContactName(common: ContactDetailsCommon): string {
  const { displayNameAs } = common;

  switch (common.type) {
    case ContactRoleType.Business:
      return common.businessName || '';
    case ContactRoleType.Individual: {
      const { individualName } = common;
      if (individualName) {
        if (displayNameAs === ContactDisplay.FirstLastName) {
          return `${individualName.firstName || ''} ${individualName.lastName || ''}`.trim();
        } else {
          return `${individualName.lastName || ''} ${individualName.firstName || ''}`.trim();
        }
      }
      break;
    }
    default:
      break;
  }

  return '';
}

export function getContactPhones(common: ContactDetailsCommon): string {
  const phones: string[] = [];

  if (common.phone1) {
    phones.push(`${common.phone1CountryCode || ''}${common.phone1}`);
  }
  if (common.phone2) {
    phones.push(`${common.phone2CountryCode || ''}${common.phone2}`);
  }

  return phones.join('   ');
}

export function getContactAddress(common: ContactDetailsCommon): string {
  return compact([common.street1, common.street2, common.city, common.postcode]).join(' ');
}

export function getFullContactAddress(common: ContactDetailsCommon): string {
  return compact([common.street1, common.street2, common.city, common.state, common.postcode]).join(' ');
}

export function calculateHourlyRate(salary: ContactDetailsEmployeeSalary): number | null {
  if (!salary.salary || !salary.hoursInPayPeriod) {
    return nullate(void 0);
  }

  let hourlyRate = 0;
  switch (salary.payrollCycle) {
    case EmployeePayroll.Weekly:
      hourlyRate = salary.salary / (52 * salary.hoursInPayPeriod);
      break;
    case EmployeePayroll.Fortnightly:
      hourlyRate = salary.salary / (26 * salary.hoursInPayPeriod);
      break;
    case EmployeePayroll.FourWeekly:
      hourlyRate = salary.salary / (13 * salary.hoursInPayPeriod);
      break;
    case EmployeePayroll.Monthly:
      hourlyRate = salary.salary / (12 * salary.hoursInPayPeriod);
      break;
    default:
      break;
  }

  if (hourlyRate) {
    return hourlyRate;
  } else {
    return nullate(void 0);
  }
}

export function trimCountryCode(phoneNumber: string, countryCode: string): string {
  return phoneNumber.startsWith(countryCode) ? phoneNumber.slice(countryCode.length) : phoneNumber;
}

export function formatPhoneNumberWithCountryCode(
  phoneNumber: string | null | undefined, 
  countryCode: string | null | undefined
): string {
  if (!phoneNumber || !countryCode) {
    return '';
  }

  try {
    const countryKey = Array.from(COUNTRY_CODES.entries())
      .find(([_, value]) => value.code === countryCode)?.[0];
    
    if (!countryKey) {
      return phoneNumber;
    }

    const countryKeyCode = countryKey as CountryCode;
    const trimmedPhoneNumber = trimCountryCode(phoneNumber, countryCode);
    const parsedNumber = parsePhoneNumberWithError(trimmedPhoneNumber, countryKeyCode);
    let formattedNumber = parsedNumber.formatInternational().slice(countryCode.length + 1);
    
    // Handle cases where country code might still be present
    if (formattedNumber.startsWith(countryCode)) {
      formattedNumber = trimCountryCode(formattedNumber, countryCode);
    }
    
    return formattedNumber;
  } catch (error) {
    console.error('Error formatting phone number:', error);
    return phoneNumber;
  }
}

export function formatContactPhones(details: ContactDetails): ContactDetails {
  const phone1 = details.common.phone1;
  const phone2 = details.common.phone2;
  const cc1 = details.common.phone1CountryCode;
  const cc2 = details.common.phone2CountryCode;

  if (cc1 && phone1) {
    details.common.phone1 = formatPhoneNumberWithCountryCode(phone1, cc1);
  }

  if (cc2 && phone2) {
    details.common.phone2 = formatPhoneNumberWithCountryCode(phone2, cc2);
  }

  return details;
}
